import { createUserAPI } from '@/utils/utils';
import { faPaperPlane, faPlus, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField
} from '@mui/material';
import { FC, ReactElement, useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { CloseIcon } from '../Icons/CommonIcons';
import { OrgContext } from '../Organizations/OrgContext';
import { OrgType } from '../Organizations/orgs.model';
import { OrgUserContext } from './OrgUserContext';
import { RoleType } from '@/API';

interface InviteUserProps {
  org: OrgType;
  canAddUser: boolean;
  userRole: RoleType;
}

export const InviteUser: FC<InviteUserProps> = ({
  org,
  canAddUser,
  userRole
}: InviteUserProps): ReactElement => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Remove this code please! This is horrible practice ~Kyle.
  const { setQueryOrgs } = useContext(OrgContext);
  const { setGqlQueryOrgs, currentOrganization } = useContext(OrgUserContext);

  type InviteUserInputs = {
    firstName: string;
    lastName: string;
    email: string;
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setError,
    clearErrors,
    watch
  } = useForm<InviteUserInputs>({
    mode: 'onSubmit'
  });

  const watchEmail = watch('email');

  useEffect(() => {
    if (watchEmail && currentOrganization?.emailDomains) {
      const emailDomain = watchEmail.split('@')[1];
      if (emailDomain && !currentOrganization.emailDomains.includes(emailDomain)) {
        setError('email', {
          type: 'manual',
          message: 'Email domain must belong to one of the organization domains'
        });
      } else {
        clearErrors('email');
      }
    }
  }, [watchEmail, currentOrganization?.emailDomains, setError, clearErrors]);

  const reg = /^[a-z0-9.+-]{1,64}@[a-z0-9.-]{1,64}$/i;

  const onSubmit = async (data: InviteUserInputs) => {
    try {
      setIsLoading(true);

      const normalizedEmail = data.email.trim().toLowerCase();
      await createUserAPI(
        org.id,
        data.firstName,
        data.lastName,
        normalizedEmail,
        userRole === RoleType.ADMIN
      );
      setQueryOrgs(true);
      setGqlQueryOrgs(true);
      setIsOpen(false);
    } catch (err: any) {
      console.log('Error in Invite User Flow', err);
      const message = err?.message || 'Unable to invite user';
      setError('email', { message });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <button
        className="btn btn-primary"
        style={{ marginLeft: '10px' }}
        onClick={() => setIsOpen(true)}
      >
        <FontAwesomeIcon icon={faPlus} className="mr-8" />
        {userRole === RoleType.ADMIN ? 'Invite Admin' : 'Invite Host'}
      </button>
      <Dialog open={isOpen} onClose={() => setIsOpen(false)} maxWidth={'md'} fullWidth={true}>
        <DialogTitle>
          <div className="text-[24px] font-bold mb-[16px]">
            {userRole === RoleType.ADMIN ? 'Invite Admin' : 'Invite Host'}
            <IconButton
              aria-label="close"
              onClick={() => setIsOpen(false)}
              sx={{
                position: 'absolute',
                right: 28,
                top: 28
              }}
            >
              <CloseIcon />
            </IconButton>
          </div>

          <div style={{ color: 'red' }}>
            {!canAddUser
              ? `No available ${userRole === RoleType.ADMIN ? 'admin' : 'host'} licenses. Please contact info@scribl.co`
              : ''}
          </div>
        </DialogTitle>
        <DialogContent className="scrollContainer">
          {
            <form>
              <div className="flex justify-between mb-[36px]">
                <span className="w-[360px]">
                  <DialogContentText className="text-black">First Name</DialogContentText>
                  <TextField
                    disabled={!canAddUser}
                    className="mb-[8px]"
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message}
                    margin="dense"
                    id="0"
                    type="text"
                    fullWidth
                    placeholder="Please Enter First Name"
                    variant="outlined"
                    autoFocus
                    {...register('firstName', {
                      required: 'First name is required'
                    })}
                  />
                </span>
                <span className="w-[360px]">
                  <DialogContentText className="text-black">Last Name</DialogContentText>
                  <TextField
                    disabled={!canAddUser}
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message}
                    className="mb-[8px]"
                    margin="dense"
                    id="1"
                    type="text"
                    fullWidth
                    variant="outlined"
                    placeholder="Please Enter Last Name"
                    autoFocus
                    {...register('lastName', {
                      required: 'Last name is required'
                    })}
                  />
                </span>
              </div>
              <div>
                <span className="w-[360px]">
                  <DialogContentText className="text-black">Email</DialogContentText>
                  <TextField
                    disabled={!canAddUser}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    className="mb-[8px]"
                    margin="dense"
                    id="3"
                    type="text"
                    fullWidth
                    autoFocus
                    variant="outlined"
                    placeholder="Please Enter Email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: reg,
                        message: 'Please enter a valid email'
                      },
                      validate: (value) => {
                        if (currentOrganization?.emailDomains) {
                          const emailDomain = value.split('@')[1];
                          return (
                            currentOrganization.emailDomains.includes(emailDomain) ||
                            'Email domain must belong to one of the organization domains'
                          );
                        }
                        return true;
                      }
                    })}
                  />
                </span>
              </div>
            </form>
          }
        </DialogContent>
        <DialogActions>
          <button
            className="btn btn-neutral mr-[5px]"
            onClick={() => {
              reset();
              setIsOpen(false);
            }}
          >
            Cancel
          </button>{' '}
          <button
            disabled={isLoading || !canAddUser}
            className="btn btn-primary"
            onClick={handleSubmit(onSubmit)}
          >
            <FontAwesomeIcon
              icon={isLoading ? faSpinner : faPaperPlane}
              spin={isLoading}
              className="mr-8"
            />
            Send{' '}
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default InviteUser;
